import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Play, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function youtubeEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export default function AdBanner() {
  const [ads, setAds] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const viewed = useRef(new Set());
  const timer = useRef(null);
  const touch = useRef({ x: 0 });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/ads`);
        setAds(res.data);
      } catch { /* sem anúncios */ }
    })();
  }, []);

  const current = ads[index];

  const trackView = useCallback((ad) => {
    if (!ad || viewed.current.has(ad.id)) return;
    viewed.current.add(ad.id);
    axios.post(`${API}/ads/${ad.id}/view`).catch(() => {});
  }, []);

  useEffect(() => {
    if (current) trackView(current);
  }, [current, trackView]);

  const next = useCallback(() => setIndex((i) => (ads.length ? (i + 1) % ads.length : 0)), [ads.length]);
  const prev = useCallback(() => setIndex((i) => (ads.length ? (i - 1 + ads.length) % ads.length : 0)), [ads.length]);

  useEffect(() => {
    if (paused || ads.length <= 1) return;
    const dur = (current?.duration_seconds || 6) * 1000;
    timer.current = setTimeout(next, dur);
    return () => clearTimeout(timer.current);
  }, [index, paused, ads.length, current, next]);

  if (ads.length === 0) return null;

  const isVideo = current.content_type === "video";
  const ytId = current.content_type === "youtube" ? youtubeEmbed(current.media_url) : null;
  const shouldAutoplay = isVideo && current.autoplay_video && !paused;

  const handleClick = () => {
    if (!current.link_url) return;
    axios.post(`${API}/ads/${current.id}/click`).catch(() => {});
    if (current.open_new_tab) {
      window.open(current.link_url, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = current.link_url;
    }
  };

  const onTouchStart = (e) => { touch.current.x = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touch.current.x;
    if (Math.abs(dx) > 50) { dx < 0 ? next() : prev(); }
  };

  return (
    <section
      data-testid="ad-banner"
      className="relative w-full bg-[#0B1E36] overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="relative w-full h-[300px] sm:h-[380px] lg:h-[440px]">
        {/* Media */}
        {ytId ? (
          <iframe
            key={current.id}
            src={`${ytId}?rel=0&modestbranding=1`}
            title={current.title || current.name}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid={`ad-media-${current.id}`}
          />
        ) : isVideo ? (
          <video
            key={current.id}
            src={current.media_url}
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay={shouldAutoplay}
            muted={shouldAutoplay}
            loop
            playsInline
            controls={paused}
            data-testid={`ad-media-${current.id}`}
          />
        ) : (
          <img
            key={current.id}
            src={current.media_url}
            alt={current.title || current.name}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            data-testid={`ad-media-${current.id}`}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1E36]/85 via-[#0B1E36]/40 to-transparent" />

        {/* Content */}
        <div className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex items-center">
          <div className="max-w-xl text-white">
            {current.title && (
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight drop-shadow-lg" data-testid={`ad-title-${current.id}`}>
                {current.title}
              </h2>
            )}
            {current.description && (
              <p className="mt-4 text-sm sm:text-base text-slate-200 leading-relaxed drop-shadow" data-testid={`ad-desc-${current.id}`}>
                {current.description}
              </p>
            )}
            {current.link_url && current.button_text && (
              <button
                onClick={handleClick}
                data-testid={`ad-cta-${current.id}`}
                className="mt-6 inline-flex items-center gap-2 bg-[#FF6A00] hover:bg-[#e65f00] text-white text-sm font-semibold px-7 py-3.5 transition-colors"
              >
                {current.button_text}
                <ExternalLink className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Arrows */}
        {ads.length > 1 && (
          <>
            <button onClick={prev} data-testid="ad-prev" aria-label="Anterior"
              className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white backdrop-blur-sm transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={next} data-testid="ad-next" aria-label="Seguinte"
              className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white backdrop-blur-sm transition-colors">
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        {/* Indicators */}
        {ads.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2" data-testid="ad-indicators">
            {ads.map((a, i) => (
              <button
                key={a.id}
                onClick={() => setIndex(i)}
                data-testid={`ad-indicator-${i}`}
                aria-label={`Anúncio ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === index ? "w-8 bg-[#FF6A00]" : "w-3 bg-white/40"}`}
              />
            ))}
          </div>
        )}

        {/* Pause hint for video */}
        {(isVideo || ytId) && (
          <div className="absolute top-3 right-3 z-20 text-white/70 text-[10px] uppercase tracking-wider bg-black/30 px-2 py-1 backdrop-blur-sm flex items-center gap-1">
            <Play className="w-3 h-3" /> Vídeo
          </div>
        )}
      </div>
    </section>
  );
}
